import type { DiffTaxonomyImportResponse } from '@/api/generatedApi';
import { DEPTH_COLORS, getDepthColor } from '../TaxonomyTree/taxonomyTreeConstants';
import { Add, FolderOpen, Label, MoveDown, Remove, WarningAmber } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { TaxonomyImportPageObjectModel } from '@/poms/taxonomyImportPageObjectModel';
import { pluralize } from '@/utils/format';

const ChangeSection = styled(Box)({
  marginBottom: '16px',
  '&:last-of-type': { marginBottom: 0 },
});

const NodeList = styled(Box)({
  maxHeight: '200px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '8px 0',
});

const ConflictList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const ConflictRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

function PathTooltip({
  name,
  parentName,
  hasChildren,
  children,
}: Readonly<{
  name: string;
  parentName?: string;
  hasChildren: boolean;
  children: React.ReactElement;
}>) {
  const segments = [...(parentName ? [parentName] : []), name];

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {segments.map((segment, index) => {
            const isLast = index === segments.length - 1;
            const color = getDepthColor(index);
            const Icon = isLast && !hasChildren ? Label : FolderOpen;
            return (
              <Box key={segment} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {index > 0 && (
                  <Typography variant='caption' sx={{ color: 'grey.400', mx: 0.25 }}>
                    →
                  </Typography>
                )}
                <Chip
                  icon={<Icon sx={{ fontSize: 14, color: `${color} !important` }} />}
                  label={segment}
                  size='small'
                  variant='outlined'
                  sx={{
                    borderColor: color,
                    color: 'common.white',
                    '& .MuiChip-label': { px: 0.75, fontSize: '11px' },
                    height: 22,
                  }}
                />
              </Box>
            );
          })}
        </Box>
      }
      arrow
      placement='top'
    >
      {children}
    </Tooltip>
  );
}

function NodeChip({
  name,
  parentName,
  hasChildren,
  opacity = 1,
}: Readonly<{
  name: string;
  parentName?: string;
  hasChildren: boolean;
  opacity?: number;
}>) {
  const color = DEPTH_COLORS[0];
  const Icon = hasChildren ? FolderOpen : Label;

  return (
    <PathTooltip name={name} parentName={parentName} hasChildren={hasChildren}>
      <Chip
        icon={<Icon sx={{ fontSize: 16, color: `${color} !important` }} />}
        label={name}
        size='small'
        variant='outlined'
        sx={{
          borderColor: color,
          '& .MuiChip-label': { px: 1 },
          height: 28,
          opacity,
        }}
      />
    </PathTooltip>
  );
}

function pluralizeNodes(count: number, hasChildren: boolean[]): string {
  const categories = hasChildren.filter(Boolean).length;
  const labels = hasChildren.filter(h => !h).length;
  if (categories > 0 && labels > 0) {
    return `${pluralize(categories, 'category', 'categories')}, ${pluralize(labels, 'label', 'labels')}`;
  }
  if (categories > 0) return pluralize(count, 'category', 'categories');
  return pluralize(count, 'label', 'labels');
}

type ConflictAction = 'remove' | 'keep' | 'rename';

interface ImportConflictStepProperties {
  diff: DiffTaxonomyImportResponse;
  isDiffing: boolean;
  diffError?: string;
  importError?: string;
  isReimporting: boolean;
  onBack: () => void;
  onReimport: (keepNodeIds: string[], renameMap: Record<string, string>) => void;
}

export const ImportConflictStep = ({
  diff,
  isDiffing,
  diffError,
  importError,
  isReimporting,
  onBack,
  onReimport,
}: ImportConflictStepProperties) => {
  const [actions, setActions] = useState<Map<string, { action: ConflictAction; renameTo?: string }>>(new Map());

  const conflictsWithLabels = diff.removals.filter(r => r.labelCount > 0);
  const removalsWithoutLabels = diff.removals.filter(r => r.labelCount === 0);
  const totalLabelCount = conflictsWithLabels.reduce((sum, r) => sum + r.labelCount, 0);

  const suggestionMap = new Map(diff.suggestedRenames.map(s => [s.removalNodeId, s]));

  const usedRenameTargets = new Set(
    [...actions.values()].filter(a => a.action === 'rename' && a.renameTo).map(a => a.renameTo!.toLowerCase())
  );
  const availableAdditions = diff.additions.map(a => a.name).filter(n => !usedRenameTargets.has(n.toLowerCase()));

  useEffect(() => {
    if (actions.size > 0) return;
    const initial = new Map<string, { action: ConflictAction; renameTo?: string }>();
    for (const removal of conflictsWithLabels) {
      const suggestion = suggestionMap.get(removal.nodeId);
      if (suggestion) {
        initial.set(removal.nodeId, { action: 'rename', renameTo: suggestion.additionName });
      } else {
        initial.set(removal.nodeId, { action: 'remove' });
      }
    }
    if (initial.size > 0) setActions(initial);
  }, []);

  if (isDiffing) {
    return (
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </DialogContent>
    );
  }

  const setAction = (nodeId: string, action: ConflictAction, renameTo?: string) => {
    setActions(previous => {
      const next = new Map(previous);
      next.set(nodeId, { action, renameTo });
      return next;
    });
  };

  const handleReimport = () => {
    const keepNodeIds: string[] = [];
    const renameMap: Record<string, string> = {};
    for (const [nodeId, entry] of actions) {
      if (entry.action === 'keep') keepNodeIds.push(nodeId);
      if (entry.action === 'rename' && entry.renameTo) renameMap[nodeId] = entry.renameTo;
    }
    onReimport(keepNodeIds, renameMap);
  };

  return (
    <>
      <DialogContent>
        {(diffError || importError) && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {diffError || importError}
          </Alert>
        )}

        {diff.unchangedCount > 0 && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 2 }}
            data-testid={TaxonomyImportPageObjectModel.ConflictUnchanged}
          >
            {diff.unchangedCount} unchanged
          </Typography>
        )}

        {diff.additions.length > 0 && (
          <ChangeSection data-testid={TaxonomyImportPageObjectModel.ConflictAdditions}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Add sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant='body2' color='success.main' fontWeight={600}>
                {pluralizeNodes(
                  diff.additions.length,
                  diff.additions.map(a => a.hasChildren)
                )}{' '}
                to add
              </Typography>
            </Box>
            <NodeList>
              {(() => {
                const byParent = new Map<string, typeof diff.additions>();
                for (const addition of diff.additions) {
                  const key = addition.parentName ?? '';
                  const group = byParent.get(key) ?? [];
                  group.push(addition);
                  byParent.set(key, group);
                }
                return [...byParent.entries()].map(([parentName, children]) => (
                  <Box key={parentName || '_root'}>
                    {parentName && (
                      <Chip
                        icon={<FolderOpen sx={{ fontSize: 14, color: 'text.disabled !important' }} />}
                        label={parentName}
                        size='small'
                        variant='outlined'
                        sx={{
                          borderColor: 'divider',
                          color: 'text.disabled',
                          '& .MuiChip-label': { px: 0.75, fontSize: '12px' },
                          height: 24,
                          mb: 0.5,
                        }}
                      />
                    )}
                    <Box sx={{ pl: parentName ? 3 : 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {children.map(addition => (
                        <Box key={addition.name}>
                          <NodeChip
                            name={addition.name}
                            parentName={addition.parentName}
                            hasChildren={addition.hasChildren}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ));
              })()}
            </NodeList>
          </ChangeSection>
        )}

        {diff.moves.length > 0 && (
          <ChangeSection data-testid={TaxonomyImportPageObjectModel.ConflictMoves}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <MoveDown sx={{ fontSize: 18, color: 'info.main' }} />
              <Typography variant='body2' color='info.main' fontWeight={600}>
                ~ {diff.moves.length} to move
              </Typography>
            </Box>
            <NodeList>
              {diff.moves.map(move => (
                <Box key={move.nodeId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NodeChip name={move.name} parentName={move.currentParentName} hasChildren={false} />
                  <Typography variant='caption' color='text.secondary'>
                    {move.currentParentName ?? 'root'} → {move.newParentName ?? 'root'}
                  </Typography>
                </Box>
              ))}
            </NodeList>
          </ChangeSection>
        )}

        {removalsWithoutLabels.length > 0 && (
          <ChangeSection data-testid={TaxonomyImportPageObjectModel.ConflictRemovals}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Remove sx={{ fontSize: 18, color: 'error.main' }} />
              <Typography variant='body2' color='error.main' fontWeight={600}>
                {pluralizeNodes(
                  removalsWithoutLabels.length,
                  removalsWithoutLabels.map(r => r.hasChildren)
                )}{' '}
                to remove
              </Typography>
            </Box>
            <NodeList>
              {removalsWithoutLabels.map(removal => (
                <Box key={removal.nodeId}>
                  <NodeChip
                    name={removal.name}
                    parentName={removal.parentName}
                    hasChildren={removal.hasChildren}
                    opacity={0.6}
                  />
                </Box>
              ))}
            </NodeList>
          </ChangeSection>
        )}

        {conflictsWithLabels.length > 0 && (
          <ChangeSection data-testid={TaxonomyImportPageObjectModel.ConflictLabeled}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <WarningAmber sx={{ fontSize: 18, color: 'warning.main' }} />
              <Typography variant='body2' color='warning.main' fontWeight={600}>
                {pluralizeNodes(
                  conflictsWithLabels.length,
                  conflictsWithLabels.map(r => r.hasChildren)
                )}{' '}
                with assets assigned
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              These are not in the imported file but have{' '}
              {pluralize(totalLabelCount, 'asset assignment', 'asset assignments')}. For each, choose to rename
              (transfers labels to the new name), keep, or remove.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                size='small'
                variant='text'
                onClick={() => {
                  const next = new Map(actions);
                  for (const r of conflictsWithLabels) next.set(r.nodeId, { action: 'keep' });
                  setActions(next);
                }}
              >
                Keep all
              </Button>
              <Button
                size='small'
                variant='text'
                onClick={() => {
                  const next = new Map(actions);
                  for (const r of conflictsWithLabels) next.set(r.nodeId, { action: 'remove' });
                  setActions(next);
                }}
              >
                Remove all
              </Button>
            </Box>
            <ConflictList>
              {conflictsWithLabels.map(removal => {
                const entry = actions.get(removal.nodeId);
                const currentAction = entry?.action ?? 'remove';
                const suggestion = suggestionMap.get(removal.nodeId);
                const currentRenameTo = entry?.renameTo ?? '';

                const dropdownOptions = [
                  ...new Set([...availableAdditions, ...(currentRenameTo ? [currentRenameTo] : [])]),
                ];

                return (
                  <ConflictRow key={removal.nodeId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <NodeChip
                        name={removal.name}
                        parentName={removal.parentName}
                        hasChildren={removal.hasChildren}
                        opacity={currentAction === 'remove' ? 0.5 : 1}
                      />
                      <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                        {pluralize(removal.labelCount, 'asset', 'assets')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {dropdownOptions.length > 0 && (
                        <Autocomplete
                          size='small'
                          options={dropdownOptions}
                          value={currentAction === 'rename' ? currentRenameTo : null} // eslint-disable-line unicorn/no-null -- MUI requires null for no selection
                          onChange={(_, value) => {
                            if (value) {
                              setAction(removal.nodeId, 'rename', value);
                            } else {
                              setAction(removal.nodeId, 'remove');
                            }
                          }}
                          renderInput={parameters => (
                            <TextField
                              {...parameters}
                              placeholder={suggestion ? `Rename to ${suggestion.additionName}...` : 'Rename to...'}
                              size='small'
                            />
                          )}
                          sx={{ width: 220 }}
                          disableClearable={false}
                        />
                      )}
                      <Button
                        size='small'
                        variant={currentAction === 'keep' ? 'outlined' : 'text'}
                        color='success'
                        onClick={() => setAction(removal.nodeId, 'keep')}
                        sx={{ minWidth: 0, px: 1.5, textTransform: 'none', fontSize: '12px' }}
                      >
                        Keep
                      </Button>
                      <Button
                        size='small'
                        variant={currentAction === 'remove' ? 'outlined' : 'text'}
                        color='error'
                        onClick={() => setAction(removal.nodeId, 'remove')}
                        sx={{ minWidth: 0, px: 1.5, textTransform: 'none', fontSize: '12px' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </ConflictRow>
                );
              })}
            </ConflictList>
          </ChangeSection>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onBack}
          disabled={isReimporting}
          data-testid={TaxonomyImportPageObjectModel.ConflictBackButton}
        >
          Back
        </Button>
        <Button
          variant='contained'
          onClick={handleReimport}
          disabled={isReimporting}
          sx={{ minWidth: '120px' }}
          data-testid={TaxonomyImportPageObjectModel.ConflictContinueButton}
        >
          {isReimporting ? <CircularProgress size={20} /> : 'Reimport'}
        </Button>
      </DialogActions>
    </>
  );
};
